import { useRef, useState, useEffect, type ReactNode } from 'react';

interface FadeInWhenVisibleProps {
  children: ReactNode;
  delay?: number;
  className?: string;
}

/**
 * Fades children in when they enter the viewport (once).
 * Uses IntersectionObserver — no framer-motion dependency.
 */
export function FadeInWhenVisible({ children, delay = 0, className }: FadeInWhenVisibleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '-60px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
        transition: `opacity 0.35s ease-out ${delay}s, transform 0.35s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}
